import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileText, Loader2, RefreshCw, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aug 2026 · Bulk re-enrich every pharma with an uploaded corporate deck so
// the Aug 2026 granular schema (therapeutic_areas_specific,
// priority_targets_moas, scientific_preferences, excluded_modalities,
// bd_contacts) gets populated fleet-wide in one click. Powers the "Deck as
// Source of Truth" reinforcement pipeline in the AI Mapping engine.
export default function DeckReenrichPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(true);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/pharma-enrich/bulk-reenrich-deck-bearing?only_missing_granular=${onlyMissing}&dry_run=true`,
        {},
        { withCredentials: true },
      );
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load deck-bearing pharma report');
    } finally {
      setLoading(false);
    }
  }, [onlyMissing]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const runBulk = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/pharma-enrich/bulk-reenrich-deck-bearing?only_missing_granular=${onlyMissing}&dry_run=false`,
        {},
        { withCredentials: true },
      );
      toast.success(
        `Queued ${data.queued_count} deck-bearing pharmas for re-enrichment. Background jobs will populate priority targets, specific indications, scientific preferences, and BD contacts over the next few minutes.`,
        { duration: 8000 },
      );
      loadReport();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk re-enrichment failed');
    } finally {
      setRunning(false);
    }
  };

  const toEnrichCount = report?.to_enrich?.length || 0;

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6" data-testid="deck-reenrich-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <FileText className="w-6 h-6 text-blue-600" /> Re-enrich Deck-Bearing Pharmas
        </h1>
        <p className="text-sm text-slate-500 mt-1.5">
          Bulk re-runs the deck-extraction LLM pass on every pharma with an uploaded corporate deck so
          the <span className="font-semibold">Aug 2026 granular schema</span> (specific indications, priority
          targets/MOAs, scientific preferences, excluded modalities, named BD contacts) gets populated fleet-wide.
          These fields power the <span className="font-semibold">Deck-as-Reinforcement</span> layer in AI Mapping —
          rationales get a verbatim citation footer from the pharma&apos;s own deck.
        </p>
      </div>

      <Card className="border-slate-100 bg-blue-50/40" data-testid="deck-reenrich-info">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-700 space-y-1">
              <p><span className="font-semibold">Why run this:</span> Pharmas whose decks were uploaded before Aug 2026 only had broad TA fields populated. The new schema extracts specific indications (NSCLC, RA, HS, IPF, MASH…), priority targets (BCMA, KATP, GLP-1 alt, molecular glues…), excluded modalities (autologous, allogeneic), and named BD contacts.</p>
              <p><span className="font-semibold">What happens:</span> Each pharma&apos;s deck(s) are re-sent to Gemini 2.5 Pro (temp=0) with the new prompt. Union-merged into the profile — no existing data is overwritten.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 text-xs">
        <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="only-missing-toggle">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
            className="rounded border-slate-300"
          />
          <span className="text-slate-600">Only re-enrich pharmas that don&apos;t yet have granular signals (skip already-processed)</span>
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : report && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-2xl font-bold text-blue-600" data-testid="to-enrich-count">{toEnrichCount}</p>
              <p className="text-xs text-slate-500">To re-enrich now</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-2xl font-bold text-emerald-700">{report.already_current_count}</p>
              <p className="text-xs text-slate-500">Already have granular signals</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900">{toEnrichCount + (report.already_current_count || 0)}</p>
              <p className="text-xs text-slate-500">Total deck-bearing pharmas</p>
            </CardContent></Card>
          </div>

          <Card className="border-slate-100">
            <CardContent className="p-5 space-y-3">
              {toEnrichCount === 0 ? (
                <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="all-current-msg">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  All deck-bearing pharmas are already enriched with the current schema.
                </p>
              ) : (
                <div data-testid="to-enrich-list">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Will be re-enriched
                  </p>
                  <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 max-h-96 overflow-y-auto">
                    {(report.to_enrich || []).map((p, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                        <span className="text-slate-800 font-medium">{p.company_name}</span>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <Badge variant="secondary" className="text-[9px]">
                            {p.deck_count} deck{p.deck_count === 1 ? '' : 's'}
                          </Badge>
                          {p.has_granular_signals && (
                            <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                              already has signals · will re-run
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={loadReport} className="text-xs" data-testid="rerun-report-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  disabled={running || toEnrichCount === 0}
                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="run-bulk-btn"
                >
                  {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  Re-enrich {toEnrichCount} deck-bearing pharma{toEnrichCount === 1 ? '' : 's'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Re-enrich {toEnrichCount} pharmas from their uploaded decks?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Each pharma&apos;s deck(s) will be re-sent to the LLM with the Aug 2026 granular schema.
                    Values are union-merged into the profile — existing data is preserved. Runs as background jobs;
                    you can close this page. Completes within a few minutes.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={runBulk} className="bg-blue-600 hover:bg-blue-700">
                    Run bulk re-enrichment
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      )}
    </div>
  );
}
